import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  ArrowRightIcon,
  CheckCircleIcon,
  LockClosedIcon,
  ShieldCheckIcon,
  SparklesIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline';
import { LandingHeader } from '../components/landing/LandingHeader';
import { LandingFooter } from '../components/landing/LandingFooter';
import { HeroPreviewCard } from '../components/landing/HeroPreviewCard';
import { BRANDING } from '../config/branding';
import { COMPANY_BRANDING } from '../config/companyBranding';
import { COMPANY_LANDING_CONTENT } from '../config/companyLandingContent';
import '../styles/landing/landing.css';
import '../styles/landing/company-landing.css';

type SegmentCard = {
  title: string;
  description: string;
  outcomes: string[];
};

const DELIVERY_SEGMENTS: SegmentCard[] = [
  {
    title: 'Hospital and Multi-Site Networks',
    description: 'Coordinate care teams, handovers, and urgent communication from one secure platform.',
    outcomes: ['Standardized communication flows', 'Cross-team escalation support'],
  },
  {
    title: 'Clinics and Specialty Practices',
    description: 'Streamline practice-level operations with less admin overhead and stronger patient continuity.',
    outcomes: ['Reduced workflow friction', 'Clear follow-up accountability'],
  },
  {
    title: 'Healthcare Innovation Teams',
    description: 'Launch secure digital pathways with modular capabilities and measurable operational impact.',
    outcomes: ['Faster implementation cycles', 'Enterprise-ready security posture'],
  },
];

type AppCard = {
  title: string;
  description: string;
  points: string[];
};

const ONECARE_APPS: AppCard[] = [
  {
    title: 'Intra-Communications System',
    description: 'Coordinate clinical and operational teams with real-time channels, urgency routing, and full audit visibility.',
    points: ['Real-time staff communication', 'Department channels and escalation', 'Audit-ready messaging'],
  },
  {
    title: 'Smart Flow AI Assistant',
    description: 'A multi-model AI assistant for clinical summaries, operational analysis, and task-based workflows with deep thinking.',
    points: ['Multi-model AI (Claude, OpenAI, Google, Kimi)', 'Task-based workflows with templates', 'Deep thinking and cost-controlled usage'],
  },
  {
    title: 'Patient Registry & AI Scribe',
    description: 'A unified inpatient, outpatient, ER, and ICU registry with structured clinical documentation and an AI scribe that turns raw notes into clinician-reviewed drafts—behind a strict de-identification layer.',
    points: ['Unified registry and encounters', 'AI scribe: paste notes to reviewed drafts', 'De-identified AI case digest and analysis'],
  },
  {
    title: 'ShiftEase Scheduling',
    description: 'A constraint-solver staff scheduler that builds fair, valid rosters and flags coverage and rest-rule conflicts before they reach the floor.',
    points: ['Automatic conflict detection', 'Fair workload and coverage balancing', 'Configurable shift and rest rules'],
  },
  {
    title: 'Notes',
    description: 'A rich-text clinical notes workspace with folders, labels, secure sharing, and one-click export to PDF or Word.',
    points: ['OneDrive-style folders and labels', 'Per-note secure sharing', 'PDF and DOCX export'],
  },
  {
    title: 'Hospital Announcements',
    description: 'Publish hospital-wide news, policy, and training updates targeted by department or role, with pinning and instant notifications.',
    points: ['Audience targeting by team', 'Pin and schedule updates', 'Instant staff notifications'],
  },
];

export const OneCareLandingPage: React.FC = () => {
  const onecareProduct = COMPANY_LANDING_CONTENT.portfolio.featured;

  useEffect(() => {
    const rootElement = document.getElementById('root');
    if (rootElement) rootElement.classList.add('company-page-scroll-root');
    document.documentElement.classList.add('company-page-scroll');
    document.body.classList.add('company-page-scroll');

    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('config', process.env.REACT_APP_GA_MEASUREMENT_ID || 'GA_MEASUREMENT_ID', {
        page_title: BRANDING.meta.defaultTitle,
        page_location: window.location.href,
        custom_map: { metric_1: 'page_load_time' },
      });
    }

    if (window.gtag) {
      window.gtag('event', 'page_view', {
        page_title: 'OneCare Landing Page',
        page_location: window.location.pathname,
        event_category: 'engagement',
      });
    }

    return () => {
      document.documentElement.classList.remove('company-page-scroll');
      document.body.classList.remove('company-page-scroll');
      const el = document.getElementById('root');
      if (el) el.classList.remove('company-page-scroll-root');
    };
  }, []);

  return (
    <>
      <Helmet>
        <title>{BRANDING.meta.defaultTitle}</title>
        <meta name="description" content={BRANDING.meta.defaultDescription} />
        <meta name="keywords" content={BRANDING.meta.keywords} />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={window.location.origin + '/onecare/'} />
        <meta property="og:title" content={BRANDING.product.fullName} />
        <meta property="og:description" content={BRANDING.meta.defaultDescription} />
        <meta property="og:url" content={window.location.origin + '/onecare/'} />
        <meta property="og:type" content="website" />
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Product',
            name: BRANDING.product.fullName,
            description: onecareProduct.description,
            url: window.location.origin + '/onecare/',
          })}
        </script>
      </Helmet>

      <div className="landing-page company-landing onecare-page">
        <LandingHeader isCompanyPage={false} />

        <section id="hero" className="company-hero" aria-labelledby="hero-heading">
          <div className="company-hero-grid" aria-hidden="true" />
          <div className="company-orb company-orb-one" aria-hidden="true" />
          <div className="company-orb company-orb-two" aria-hidden="true" />
          <div className="company-orb company-orb-three" aria-hidden="true" />
          <div className="company-orb company-orb-four" aria-hidden="true" />

          <div className="company-shell company-hero-layout">
            <div className="company-hero-copy">
              <p className="company-eyebrow">{BRANDING.product.fullName}</p>
              <h1 id="hero-heading" className="company-hero-title">
                <span className="company-hero-title-line1">Hospital operations,</span>
                <span className="company-hero-title-line2">unified</span>
              </h1>
              <p className="company-hero-subline">
                Real-time communication, workflow intelligence, and secure clinical operations in one platform.
              </p>

              <div className="company-hero-actions">
                <Link to="/onecare/login" className="company-btn company-btn-primary">
                  Sign in
                  <ArrowRightIcon className="company-btn-icon" aria-hidden="true" />
                </Link>
                <Link to="/contact" className="company-btn company-btn-secondary">
                  Talk to our team
                </Link>
              </div>

              <div className="company-trust-chips" aria-label="Trust and security highlights">
                <span className="company-chip">
                  <ShieldCheckIcon className="company-chip-icon" aria-hidden="true" />
                  Enterprise-grade security
                </span>
                <span className="company-chip">
                  <LockClosedIcon className="company-chip-icon" aria-hidden="true" />
                  Domain-isolated architecture
                </span>
                <span className="company-chip">
                  <SparklesIcon className="company-chip-icon" aria-hidden="true" />
                  Workflow intelligence
                </span>
              </div>

              <div className="company-hero-stats" aria-label="Operational highlights">
                {onecareProduct.stats.slice(0, 3).map((stat) => (
                  <div key={stat.label} className="company-hero-stat">
                    <strong>{stat.value}</strong>
                    <span>{stat.label}</span>
                  </div>
                ))}
              </div>
            </div>
            <HeroPreviewCard />
          </div>
        </section>

        <main id="main-content" role="main" className="company-main">
          <section id="solutions" className="company-section">
            <div className="company-shell">
              <header className="company-section-header">
                <p className="company-section-eyebrow">Platform</p>
                <h2>One platform. Every app your teams need.</h2>
                <p>
                  Real-time coordination, AI-assisted clinical work, scheduling, and secure operations—shipped together and built for production.
                </p>
              </header>

              <div className="company-card-grid">
                {ONECARE_APPS.map((app) => (
                  <article key={app.title} className="company-card">
                    <h3>{app.title}</h3>
                    <p>{app.description}</p>
                    <ul>
                      {app.points.map((point) => (
                        <li key={point}>
                          <CheckCircleIcon className="company-list-icon" aria-hidden="true" />
                          <span>{point}</span>
                        </li>
                      ))}
                    </ul>
                  </article>
                ))}
              </div>
            </div>
          </section>

          <section id="modules" className="company-section company-section-soft">
            <div className="company-shell">
              <header className="company-section-header">
                <p className="company-section-eyebrow">Product Suite</p>
                <h2>{onecareProduct.name}</h2>
                <p>{onecareProduct.tagline}</p>
              </header>

              <div className="company-suite-layout">
                <article className="company-suite-card company-suite-card-primary">
                  <div className="company-suite-label">Featured platform</div>
                  <h3>{onecareProduct.description}</h3>
                  <div className="company-suite-list">
                    {onecareProduct.features.map((feature) => (
                      <p key={feature}>
                        <CheckCircleIcon className="company-list-icon" aria-hidden="true" />
                        <span>{feature}</span>
                      </p>
                    ))}
                  </div>
                  <div className="company-hero-actions">
                    <Link to={onecareProduct.loginLink} className="company-btn company-btn-primary">
                      Sign in to OneCare
                      <ArrowRightIcon className="company-btn-icon" aria-hidden="true" />
                    </Link>
                    <Link to="/contact" className="company-btn company-btn-secondary">
                      Contact Sales
                    </Link>
                  </div>
                </article>

                <article className="company-suite-card">
                  <div className="company-stack-title">
                    <SparklesIcon className="company-stack-icon" aria-hidden="true" />
                    <h3>Healthcare software stack</h3>
                  </div>
                  <div className="company-stack-grid">
                    <div>
                      <strong>Communication</strong>
                      <p>Urgent messaging, care-team channels, and escalation workflows.</p>
                    </div>
                    <div>
                      <strong>Operations</strong>
                      <p>Duty planning, workflow orchestration, and accountable task routing.</p>
                    </div>
                    <div>
                      <strong>Clinical records</strong>
                      <p>Patient registry, structured documentation, and AI scribe drafting.</p>
                    </div>
                    <div>
                      <strong>Governance</strong>
                      <p>Role-based control, audit logs, and policy-aligned data access.</p>
                    </div>
                  </div>
                </article>
              </div>
            </div>
          </section>

          <section id="customers" className="company-section">
            <div className="company-shell">
              <header className="company-section-header">
                <p className="company-section-eyebrow">Customers</p>
                <h2>Built for teams solving real-world healthcare practice challenges</h2>
                <p>
                  We partner with providers, administrators, and digital transformation teams that need secure tools that actually fit daily care operations.
                </p>
              </header>

              <div className="company-segment-grid">
                {DELIVERY_SEGMENTS.map((segment) => (
                  <article key={segment.title} className="company-segment-card">
                    <div className="company-segment-heading">
                      <UserGroupIcon className="company-stack-icon" aria-hidden="true" />
                      <h3>{segment.title}</h3>
                    </div>
                    <p>{segment.description}</p>
                    <ul>
                      {segment.outcomes.map((outcome) => (
                        <li key={outcome}>
                          <CheckCircleIcon className="company-list-icon" aria-hidden="true" />
                          <span>{outcome}</span>
                        </li>
                      ))}
                    </ul>
                  </article>
                ))}
              </div>
            </div>
          </section>

          <section id="updates" className="company-section company-updates">
            <div className="company-shell">
              <header className="company-section-header">
                <p className="company-section-eyebrow">What&apos;s New</p>
                <h2>Future-ready innovation with enterprise healthcare discipline</h2>
                <p>
                  OneCare continues to evolve around secure communication, intelligent automation, and scalable operations for healthcare organizations.
                </p>
              </header>

              <div className="company-card-grid" style={{ marginBottom: '2.5rem' }}>
                <article className="company-card">
                  <h3>Patient Registry & AI Scribe</h3>
                  <p>A unified inpatient, outpatient, ER, and ICU registry with structured documentation, an ADT workflow, and an AI scribe that drafts reviewed notes—every AI path behind a strict de-identification layer.</p>
                </article>
                <article className="company-card">
                  <h3>Smart Flow AI</h3>
                  <p>Multi-model AI assistant with task-based workflows, deep thinking toggle, real-time streaming, and per-session cost tracking across 4 providers and 18+ models.</p>
                </article>
                <article className="company-card">
                  <h3>Feature Access Control</h3>
                  <p>Per-domain feature enablement with 4-flag model, multi-layer caching, and immediate invalidation so administrators control exactly what each hospital sees.</p>
                </article>
              </div>

              <div className="company-updates-layout">
                <div />
                <div className="company-updates-actions">
                  <Link to="/onecare/login" className="company-btn company-btn-primary">
                    Sign in to OneCare
                    <ArrowRightIcon className="company-btn-icon" aria-hidden="true" />
                  </Link>
                  <Link to="/contact" className="company-btn company-btn-secondary">
                    Contact Sales
                  </Link>
                </div>
              </div>
            </div>
          </section>
        </main>

        <LandingFooter isCompanyPage={false} />
      </div>
    </>
  );
};

export default OneCareLandingPage;
